import { IThemeService } from '@hibro/shared';
import { Button, Icon } from '@hibro/ui-client';
import { type ReactNode, useCallback } from 'react';

import { useTranslator } from '../../i18n/i18n';
import { useWorkbenchLayout } from '../layout-context';
import { HeaderCreate, type HeaderCreateProps } from './header-create';
import { SHOW_UNFINISHED_HEADER_CHROME } from './unfinished-chrome';

interface HeaderRightProps {
  onRefresh: () => void;
  themeService: IThemeService;
  /** Commands placed at `MENU_LOCATION.headerCreate` (见 `header-create`). */
  create: HeaderCreateProps;
}

interface HeaderIconButtonProps {
  iconId: string;
  label: string;
  onClick?: () => void;
}

function HeaderIconButton(props: HeaderIconButtonProps): ReactNode {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      title={props.label}
      aria-label={props.label}
      onClick={props.onClick}
      className="cursor-pointer size-8 text-(--app-color-text-secondary) "
      style={{ WebkitAppRegion: 'no-drag' }}
    >
      <Icon
        className="size-5"
        spec={{ type: 'builtin', id: props.iconId }}
        size={20}
      />
    </Button>
  );
}
/**
 * Right-side action cluster of the header (架构方案 §5.7): collapsible-area
 * toggles (`panel`, gated by `enabledAreas`), the Create menu (gated by whether
 * any module placed a command there, see `HeaderCreate`), and icon buttons for
 * reset-layout, refresh, notifications and the light/dark theme switch.
 * Unfinished actions stay in the tree, gated by `import.meta.env.DEV`.
 * Consumes the public `WorkbenchLayoutApi` via `useWorkbenchLayout()` so it can
 * live anywhere in the workbench tree without prop drilling; the theme toggle
 * flips `IThemeService`'s preference.
 */
export function HeaderRight({
  onRefresh,
  themeService,
  create,
}: HeaderRightProps): ReactNode {
  const { t } = useTranslator();
  const { openAreas, enabledAreas, toggle, reset } = useWorkbenchLayout();
  // Areas disabled by the schema (`enabled: false`) render no control at all.

  const onChangeTheme = useCallback(async () => {
    const theme = await themeService.getTheme();
    void (await themeService.setPreference(
      theme.type === 'light' ? 'dark' : 'light',
    ));
  }, [themeService]);

  return (
    <div className="flex items-center gap-1">
      {enabledAreas.panel ? (
        <Button
          type="button"
          variant={openAreas.panel ? 'default' : 'ghost'}
          size="sm"
          aria-pressed={openAreas.panel}
          onClick={() => {
            toggle('panel');
          }}
          style={{ WebkitAppRegion: 'no-drag' }}
        >
          {t('workbench.action.togglePanel')}
        </Button>
      ) : null}

      <HeaderCreate entries={create.entries} onRun={create.onRun} />
      <HeaderIconButton
        iconId="layout"
        label={t('workbench.action.resetLayout')}
        onClick={reset}
      />
      {SHOW_UNFINISHED_HEADER_CHROME ? (
        <HeaderIconButton
          iconId="refresh"
          label={t('workbench.action.refresh')}
          onClick={onRefresh}
        />
      ) : null}
      {SHOW_UNFINISHED_HEADER_CHROME ? (
        <HeaderIconButton
          iconId="notification"
          label={t('workbench.action.notifications')}
        />
      ) : null}
      <HeaderIconButton
        iconId="theme"
        label={t('workbench.action.toggleTheme')}
        onClick={() => {
          void onChangeTheme();
        }}
      />
    </div>
  );
}
